import { SmartTableColumn, WithNAFallback } from '@toggle/design-system';
import { formatPercentage, getCurrencySymbol } from '@toggle/helpers';
import { EarningsItemBase, FinancialData } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatNumberWithUnit } from '~/widgets/earnings/utils/utils';

import { SharedTable } from '../../../components/shared-table/SharedTable';
import * as S from './EarningsHighlightsTable.styles';

export interface EarningsHighlightsTableProps {
  earningsData: EarningsItemBase;
}

export const EarningsHighlightsTable = ({
  earningsData,
}: EarningsHighlightsTableProps) => {
  const { t } = useTranslation(['earnings']);
  const { eps, revenue } = earningsData;

  const naContainerPadding = '10px 12px';

  const tableData = [
    {
      name: t('earnings:earningsInsight.drawer.earningsRecap.tables.eps'),
      key: 'eps',
      ...eps,
    },
    {
      name: t('earnings:earningsInsight.drawer.earningsRecap.tables.revenue'),
      key: 'revenue',
      ...revenue,
    },
  ];

  const tableColumns: SmartTableColumn<
    FinancialData & { name: string; key: string }
  >[] = [
    {
      label: 'Name',
      key: 'name',
      renderHeader: () => null,
      render: value => <S.StyledNameCell>{value}</S.StyledNameCell>,
    },
    {
      label: t('earnings:earningsInsight.drawer.earningsRecap.tables.estimate'),
      key: 'estimate',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(earningsData.currency);
        return (
          <WithNAFallback
            containerPadding={naContainerPadding}
            value={
              value && (
                <S.NumberCell>{`${currencySymbol}${formatNumberWithUnit(
                  value
                )}`}</S.NumberCell>
              )
            }
          />
        );
      },
    },
    {
      label: t('earnings:earningsInsight.drawer.earningsRecap.tables.reported'),
      key: 'value',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(earningsData.currency);
        return (
          <WithNAFallback
            containerPadding={naContainerPadding}
            value={
              value && (
                <S.NumberCell>{`${currencySymbol}${formatNumberWithUnit(
                  value
                )}`}</S.NumberCell>
              )
            }
          />
        );
      },
    },
    {
      label: t('earnings:earningsInsight.drawer.earningsRecap.tables.surprise'),
      key: 'surprise',
      alignment: 'right',
      render: value => {
        if (!value) {
          return (
            <WithNAFallback
              containerPadding={naContainerPadding}
              value={undefined}
            />
          );
        }

        const currencySymbol = getCurrencySymbol(earningsData.currency);
        const formatted = formatNumberWithUnit(value);
        const displayValue = formatted.startsWith('-')
          ? `-${currencySymbol}${formatted.slice(1)}`
          : `${currencySymbol}${formatted}`;

        return (
          <WithNAFallback
            containerPadding={naContainerPadding}
            value={
              <S.SurpriseCell $isPositive={value > 0} $isZero={value === 0}>
                {displayValue}
              </S.SurpriseCell>
            }
          />
        );
      },
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsRecap.tables.surprisePercentage'
      ),
      key: 'surprise_percentage',
      alignment: 'right',
      render: value => (
        <WithNAFallback
          containerPadding={naContainerPadding}
          value={
            value && (
              <S.SurpriseCell $isPositive={value > 0} $isZero={value === 0}>
                {formatPercentage(value, {
                  suffix: '%',
                  addPlus: true,
                  useOriginalValue: true,
                })}
              </S.SurpriseCell>
            )
          }
        />
      ),
    },
  ];
  return (
    <SharedTable
      title={t(
        'earnings:earningsInsight.drawer.earningsRecap.earningsHighlights'
      )}
      tableData={tableData}
      tableColumns={tableColumns}
      noCellPadding
    />
  );
};
